import { useState } from "react";
import { Disclosure, DisclosureButton, DisclosurePanel, Textarea } from "@headlessui/react";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/solid";
import { GetTraitFeedback, GetTraitLlmFeedback } from "../../../app/writingAnalysis";
import { TraitScore } from "../../writing/drafting/TraitScore";
import { TraitFeedback } from "../../writing/drafting/TraitFeedback";
import { TraitLlmFeedback } from "../../writing/drafting/TraitLlmFeedback";
import { TraitTutorials } from "../../writing/drafting/TraitTutorials";
import {
	DraftScoreCategoryEditModel,
	ScoredCategoryViewModel,
	useEditDraftScoreCategoryMutation,
	WritingAnalysisViewModel,
} from "../scoreReportApiSlice";

interface Props {
	className?: string;
	analysis: WritingAnalysisViewModel;
	onProctorScore: (trait: ScoredCategoryViewModel) => void;
}

export default function WritingAnalysis(props: Props) {
	if (!props.analysis.categories) {
		return (
			<>
				<h1 className={props.className}>Writing Analysis</h1>
				<p>This draft does not have writing analysis.</p>
			</>
		);
	}

	return (
		<>
			<h1 className={props.className}>Writing Analysis</h1>
			{props.analysis.categories
				.filter((trait) => !trait.isProctorScored && trait.score)
				.map((trait) => {
					return (
						<TraitAccordion key={trait.id} trait={trait} analysis={props.analysis} />
					);
				})}
			{props.analysis.categories
				.filter(
					(trait) =>
						trait.isProctorScored &&
						(trait.score || props.analysis.canScoreProctorScorableCategories)
				)
				.map((trait) => {
					return (
						<TraitAccordion
							key={trait.id}
							trait={trait}
							analysis={props.analysis}
							onUpdate={props.onProctorScore}
						/>
					);
				})}
		</>
	);
}

interface TraitRowProps {
	className?: string;
	open?: boolean;
	analysis: WritingAnalysisViewModel;
	trait: ScoredCategoryViewModel;
	onUpdate?: (trait: ScoredCategoryViewModel) => void;
}

function TraitAccordion({ analysis, trait, onUpdate }: TraitRowProps) {
	return (
		<Disclosure as="div" defaultOpen={true} key={trait.id} className="pt-6">
			{({ open }) => (
				<>
					<dt>
						<DisclosureButton
							className={`flex w-full items-center justify-between text-left text-gray-900 p-4 border border-gray-200 bg-gray-50 rounded-md
								${open ? "border-b-0 rounded-b-none" : ""}`}>
							<TraitAccordionHeader trait={trait} open={open} analysis={analysis} />
							<span className="ml-6 flex h-7 items-center">
								{open ? (
									<MinusIcon
										className="h-5 w-5 print:!hidden"
										aria-hidden="true"
									/>
								) : (
									<PlusIcon
										className="h-5 w-5 print:!hidden"
										aria-hidden="true"
									/>
								)}
							</span>
						</DisclosureButton>
					</dt>

					<TraitAccordionBody
						trait={trait}
						analysis={analysis}
						className="border border-gray-200 border-t-0 rounded-b-md"
						open={open}
						onUpdate={onUpdate}
					/>
				</>
			)}
		</Disclosure>
	);
}
function TraitAccordionHeader({ trait, analysis, open }: TraitRowProps) {
	return (
		<div className="flex justify-between items-center w-full">
			<h2>{trait.name}</h2>
			{!open && (
				<TraitScore
					score={trait.score}
					maxScore={trait.maxScore}
					visible={analysis.showScore}
					oneline={false}
				/>
			)}
		</div>
	);
}

function TraitAccordionBody({ trait, analysis, className, onUpdate }: TraitRowProps) {
	const canBeEdited = trait.isProctorScored && analysis.canScoreProctorScorableCategories;

	return (
		<DisclosurePanel as="dd" className={`${className} pr-12 p-4`}>
			{canBeEdited ? (
				<EditableTraitBody trait={trait} analysis={analysis} onUpdate={onUpdate} />
			) : (
				<StaticTraitBody trait={trait} analysis={analysis} />
			)}
		</DisclosurePanel>
	);
}

function StaticTraitBody({ trait, analysis }: TraitRowProps) {
	const feedback = GetTraitFeedback(analysis, trait);
	const llmFeedback = GetTraitLlmFeedback(analysis, trait);
	const feedbackHeading = trait.isProctorScored
		? "Teacher Evaluation"
		: "Evaluation and Feedback";

	return (
		<section key={trait.id} className="flex print:!flex-col">
			<div className="w-5/12 print:!w-full">
				<TraitScore
					score={trait.score!}
					maxScore={trait.maxScore}
					visible={analysis.showScore}
					oneline={false}
				/>
				<TraitLlmFeedback
					className="mt-4 mr-4"
					heading="Writing Advisor"
					llmFeedbackId={analysis.llmFeedback?.id}
					llmFeedback={llmFeedback}
					traitId={trait.id}
					showCommentButton={analysis.llmFeedback?.id !== undefined}
				/>
			</div>
			<div className="w-7/12 ml-4 print:!w-full print:!ml-0 print:!mt-2">
				<TraitFeedback
					heading={feedbackHeading}
					feedback={feedback}
					isProctorScored={trait.isProctorScored}
				/>
				<TraitTutorials
					className="mt-4"
					heading="Lessons"
					tutorials={analysis.feedbackTutorials[trait.id]}
				/>
			</div>
		</section>
	);
}

function EditableTraitBody({ trait, analysis, onUpdate }: TraitRowProps) {
	const [isEditing, setIsEditing] = useState(false);
	const [score, setScore] = useState(trait.score);
	const [feedback, setFeedback] = useState(trait.feedback);

	const [callEditDraftScoreCategory] = useEditDraftScoreCategoryMutation();

	const handleEditClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setIsEditing(true);
	};

	const handleSaveClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		if (!score) return;

		setIsEditing(false);

		const model: DraftScoreCategoryEditModel = {
			draftScoreCategoryId: trait.draftScoreCategoryId,
			categoryId: trait.id,
			scoreId: analysis.id,
			value: score,
			proctorFeedback: feedback,
		};

		callEditDraftScoreCategory(model)
			.unwrap()
			.then((result) => {
				const _trait = {
					...trait,
					draftScoreCategoryId: result.draftScoreCategoryId,
					feedback: feedback,
					score: score,
				};
				onUpdate && onUpdate(_trait);
			})
			.catch((error) => {
				console.log(`error calling callEditDraftScoreCategory: ${error}`);
			});
	};

	const handleCancelClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		setScore(trait.score);
		setFeedback(trait.feedback);
		setIsEditing(false);
	};

	const handleTraitScoreUpdate = (score: number) => {
		setScore(score);
	};

	const isSaveDisabled = !score;

	return (
		<section key={trait.id} className="flex">
			<div className="w-5/12">
				<TraitScore
					score={score}
					maxScore={trait.maxScore}
					visible={analysis.showScore}
					oneline={false}
					readonly={!isEditing}
					onUpdate={handleTraitScoreUpdate}
				/>
				<div className="block mt-2">
					{isEditing ? (
						<>
							<button
								className="btn btn-primary mt-4"
								onClick={handleSaveClick}
								disabled={isSaveDisabled}>
								Save Score and Evaluation
							</button>
							<button
								className="btn btn-neutral ml-4 mt-4"
								onClick={handleCancelClick}>
								Cancel
							</button>
						</>
					) : (
						<button className="btn btn-neutral ml-auto mt-4" onClick={handleEditClick}>
							Score {trait.name}
						</button>
					)}
				</div>
			</div>
			<div className="w-7/12">
				<h3>Teacher Evaluation</h3>
				{feedback || isEditing ? (
					<Textarea
						className={`block w-full mt-2 ${isEditing ? "" : "border-none resize-none p-0"}`}
						value={feedback ?? ""}
						disabled={!isEditing}
						onChange={(e) => setFeedback(e.target.value)}
					/>
				) : (
					<div className="italic mt-2">Your teacher has not provided any feedback.</div>
				)}
			</div>
		</section>
	);
}
